import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  CaretLeft,
  Share,
  Copy,
  Gift,
  Users,
  Coffee,
  Check,
  WhatsappLogo,
  TelegramLogo,
  Envelope,
  Link as LinkIcon
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════
// COLORS
// ═══════════════════════════════════════════════════════════
const COLORS = {
  bg: "#F5E6E0",
  green: "#2D5A3D",
  orange: "#E07A3D",
  tan: "#D4A574",
  text: "#1A1A1A",
  textMuted: "#6B5B4F",
  white: "#FFFFFF",
};

// ═══════════════════════════════════════════════════════════
// REFERRAL PAGE
// ═══════════════════════════════════════════════════════════
export default function ReferralPage() {
  const navigate = useNavigate();
  const { user, token, setShowAuthModal } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [referralHistory, setReferralHistory] = useState([]);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    loadReferralData();
  }, [user]);

  const loadReferralData = async () => {
    try {
      const [codeRes, historyRes] = await Promise.all([
        axios.get(`${API}/api/referral/my-code`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/referral/history`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setReferralData(codeRes.data);
      setReferralHistory(historyRes.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const copyCode = () => {
    if (referralData?.referral_code) {
      navigator.clipboard.writeText(referralData.referral_code);
      setCopied(true);
      toast.success("Code kopiert!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareVia = (platform) => {
    if (!referralData) return;
    
    const text = referralData.share_text;
    const url = `${window.location.origin}?ref=${referralData.referral_code}`;
    
    let shareUrl = "";
    switch (platform) {
      case "whatsapp":
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + "\n" + url)}`;
        break;
      case "telegram":
        shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
        break;
      case "email":
        shareUrl = `mailto:?subject=${encodeURIComponent("Franky's Farm Einladung")}&body=${encodeURIComponent(text + "\n\n" + url)}`;
        break;
      default:
        if (navigator.share) {
          navigator.share({ title: "Franky's Farm", text, url });
          return;
        }
        copyCode();
        return;
    }
    
    window.open(shareUrl, "_blank");
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: COLORS.bg }}>
        <p style={{ color: COLORS.textMuted }}>Bitte zuerst anmelden...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: COLORS.bg }}>
      {/* Header */}
      <header className="px-4 pt-6 pb-4 flex items-center gap-3">
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: COLORS.white }}
        >
          <CaretLeft size={22} weight="bold" style={{ color: COLORS.text }} />
        </motion.button>
        <div>
          <h1 
            className="text-xl font-black"
            style={{ color: COLORS.text, fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Freunde einladen
          </h1>
          <p className="text-sm" style={{ color: COLORS.textMuted }}>
            Teile & verdiene Stempel
          </p>
        </div>
      </header>

      <main className="px-4 space-y-4">
        {/* Hero Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 rounded-3xl text-center relative overflow-hidden"
          style={{ backgroundColor: COLORS.green }}
        >
          {/* Floating decoration */}
          <motion.div
            className="absolute top-4 right-4"
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          >
            <Gift size={32} weight="fill" className="text-white/20" />
          </motion.div>
          
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
          >
            <Users size={40} weight="fill" className="text-white" />
          </motion.div>
          
          <h2 
            className="text-2xl font-black text-white mb-2"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Teile & Verdiene!
          </h2>
          <p className="text-white/80 mb-4">
            Für jeden eingeladenen Freund bekommst du
          </p>
          
          <motion.div
            className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl"
            style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <span className="text-3xl">☕☕☕</span>
            <span className="text-xl font-black text-white">3 Stempel!</span>
          </motion.div>
        </motion.div>

        {/* Referral Code Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-5 rounded-3xl"
          style={{ backgroundColor: COLORS.white }}
        >
          <p 
            className="text-sm font-bold mb-2 text-center"
            style={{ color: COLORS.textMuted }}
          >
            Dein Einladungs-Code
          </p>
          
          <motion.div
            className="p-4 rounded-2xl flex items-center justify-between mb-4"
            style={{ backgroundColor: COLORS.bg }}
          >
            <span 
              className="text-2xl font-black font-mono tracking-wider"
              style={{ color: COLORS.green }}
            >
              {loading ? "..." : referralData?.referral_code || "---"}
            </span>
            
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={copyCode}
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: COLORS.green }}
            >
              {copied ? (
                <Check size={24} weight="bold" className="text-white" />
              ) : (
                <Copy size={24} weight="bold" className="text-white" />
              )}
            </motion.button>
          </motion.div>

          {/* Share Buttons */}
          <div className="grid grid-cols-4 gap-3">
            <ShareButton 
              icon={WhatsappLogo} 
              color="#25D366" 
              label="WhatsApp"
              onClick={() => shareVia("whatsapp")}
            />
            <ShareButton 
              icon={TelegramLogo} 
              color="#0088cc" 
              label="Telegram"
              onClick={() => shareVia("telegram")}
            />
            <ShareButton 
              icon={Envelope} 
              color={COLORS.orange} 
              label="E-Mail"
              onClick={() => shareVia("email")}
            />
            <ShareButton 
              icon={LinkIcon} 
              color={COLORS.tan} 
              label="Link"
              onClick={() => shareVia("link")}
            />
          </div>
        </motion.div>

        {/* Stats Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-5 rounded-3xl"
          style={{ backgroundColor: COLORS.white }}
        >
          <h3 
            className="font-bold mb-4 flex items-center gap-2"
            style={{ color: COLORS.text, fontFamily: "'Space Grotesk', sans-serif" }}
          >
            <Coffee size={20} weight="fill" style={{ color: COLORS.green }} />
            Deine Statistik
          </h3>
          
          <div className="grid grid-cols-2 gap-3">
            <div 
              className="p-4 rounded-xl text-center"
              style={{ backgroundColor: COLORS.bg }}
            >
              <p className="text-3xl font-black" style={{ color: COLORS.green }}>
                {referralData?.referral_count || 0}
              </p>
              <p className="text-sm" style={{ color: COLORS.textMuted }}>
                Freunde eingeladen
              </p>
            </div>
            <div 
              className="p-4 rounded-xl text-center"
              style={{ backgroundColor: COLORS.bg }}
            >
              <p className="text-3xl font-black" style={{ color: COLORS.orange }}>
                {referralData?.stamps_earned || 0}
              </p>
              <p className="text-sm" style={{ color: COLORS.textMuted }}>
                Stempel verdient
              </p>
            </div>
          </div>
        </motion.div>

        {/* Referral History */}
        {referralHistory.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-5 rounded-3xl"
            style={{ backgroundColor: COLORS.white }}
          >
            <h3 
              className="font-bold mb-4"
              style={{ color: COLORS.text, fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Letzte Einladungen
            </h3>
            
            <div className="space-y-3">
              {referralHistory.slice(0, 5).map((ref, i) => (
                <motion.div
                  key={ref.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-center gap-3 p-3 rounded-xl"
                  style={{ backgroundColor: COLORS.bg }}
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                    style={{ backgroundColor: COLORS.green, color: COLORS.white }}
                  >
                    {ref.referred_name?.charAt(0).toUpperCase() || "?"}
                  </div>
                  <div className="flex-1">
                    <p className="font-bold" style={{ color: COLORS.text }}>
                      {ref.referred_name}
                    </p>
                    <p className="text-xs" style={{ color: COLORS.textMuted }}>
                      +{ref.stamps_given} Stempel erhalten
                    </p>
                  </div>
                  <span className="text-lg">☕</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* How it Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="p-5 rounded-3xl"
          style={{ backgroundColor: COLORS.orange + "15" }}
        >
          <h3 
            className="font-bold mb-3"
            style={{ color: COLORS.orange, fontFamily: "'Space Grotesk', sans-serif" }}
          >
            So funktioniert's
          </h3>
          
          <ul className="space-y-2 text-sm" style={{ color: COLORS.textMuted }}>
            <li className="flex items-start gap-2">
              <span className="font-bold" style={{ color: COLORS.orange }}>1.</span>
              <span>Teile deinen Code mit Freunden</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold" style={{ color: COLORS.orange }}>2.</span>
              <span>Dein Freund registriert sich mit dem Code</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold" style={{ color: COLORS.orange }}>3.</span>
              <span>Du bekommst automatisch 3 Stempel</span>
            </li>
          </ul>
        </motion.div>
      </main>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SHARE BUTTON COMPONENT
// ═══════════════════════════════════════════════════════════
const ShareButton = ({ icon: Icon, color, label, onClick }) => (
  <motion.button
    whileTap={{ scale: 0.9 }}
    onClick={onClick}
    className="flex flex-col items-center gap-1 py-3 rounded-xl"
    style={{ backgroundColor: color + "15" }}
  >
    <Icon size={28} weight="fill" style={{ color }} />
    <span className="text-xs font-bold" style={{ color }}>{label}</span>
  </motion.button>
);
